import React from 'react';
import {
  ChevronRight,
} from 'lucide-react';
import {SubscriptionPlan} from '../constants';
import styles from '../Settings.module.css';

interface SettingsSecurityBillingSectionProps {
  currentPlan: SubscriptionPlan;
  manageLoading: boolean;
  managementAvailable: boolean;
  onManageSubscription: () => void;
}

const securityHeaderIconStyle = {
  '--settings-security-icon-url': `url("${process.env.PUBLIC_URL}/icons/security.svg")`,
} as React.CSSProperties;

const billingHeaderIconStyle = {
  '--settings-billing-icon-url': `url("${process.env.PUBLIC_URL}/icons/bill.svg")`,
} as React.CSSProperties;

const ActionButton: React.FC<{
  children: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
}> = ({children, disabled = false, onClick}) => (
  <button type="button" className={styles.compactActionButton} disabled={disabled} onClick={onClick}>
    <span>{children}</span>
    <ChevronRight size={15} aria-hidden="true" />
  </button>
);

const SettingsSecurityBillingSection: React.FC<SettingsSecurityBillingSectionProps> = ({
  currentPlan,
  manageLoading,
  managementAvailable,
  onManageSubscription,
}) => (
  <>
    <section className={`${styles.settingsCard} ${styles.compactSettingsCard} ${styles.securityCard}`}>
      <div className={styles.sectionHeadingRow}>
        <span className={styles.sectionIcon}>
          <span className={styles.securityHeaderIconAsset} style={securityHeaderIconStyle} aria-hidden="true" />
        </span>
        <div>
          <h3 className={styles.settingsCardTitle}>Security</h3>
          <p className={styles.settingsCardSubtitle}>Keep your account secure.</p>
        </div>
      </div>

      <div className={styles.compactSettingRow}>
        <div><strong>Password</strong><span>Update your password regularly.</span></div>
        <ActionButton>Change Password</ActionButton>
      </div>
      <div className={styles.compactSettingRow}>
        <div><strong>Two-Step Verification</strong><span>Add an extra layer of security.</span></div>
        <button type="button" className={styles.enabledAction}>
          <span>Enabled</span><ChevronRight size={15} aria-hidden="true" />
        </button>
      </div>
      <div className={styles.compactSettingRow}>
        <div><strong>Login Activity</strong><span>View and manage your active sessions.</span></div>
        <ActionButton>View Sessions</ActionButton>
      </div>
    </section>

    <section className={`${styles.settingsCard} ${styles.compactSettingsCard} ${styles.billingCard}`}>
      <div className={styles.sectionHeadingRow}>
        <span className={styles.sectionIcon}>
          <span className={styles.billingHeaderIconAsset} style={billingHeaderIconStyle} aria-hidden="true" />
        </span>
        <div>
          <h3 className={styles.settingsCardTitle}>Billing</h3>
          <p className={styles.settingsCardSubtitle}>Manage your payment methods and history.</p>
        </div>
      </div>

      <div className={styles.compactSettingRow}>
        <div><strong>Payment Methods</strong><span>Add or update your payment methods.</span></div>
        <ActionButton
          disabled={manageLoading || (currentPlan === 'pro' && !managementAvailable)}
          onClick={currentPlan === 'pro' ? onManageSubscription : undefined}
        >
          {manageLoading ? 'Opening...' : 'Manage'}
        </ActionButton>
      </div>
      <div className={styles.compactSettingRow}>
        <div><strong>Billing History</strong><span>View your invoices and receipts.</span></div>
        <ActionButton>View History</ActionButton>
      </div>
      <div className={styles.compactSettingRow}>
        <div><strong>Promo Codes</strong><span>Apply a promo code to your account.</span></div>
        <ActionButton>Redeem Code</ActionButton>
      </div>
    </section>
  </>
);

export default React.memo(SettingsSecurityBillingSection);
